import React, { useState, useEffect } from 'react';
import { TelegramBotsList } from './TelegramBotsList';
import { TelegramBotForm } from './TelegramBotForm';
import { telegramBotsApi } from '../../utils/admin/telegramApi';
import { TelegramBot } from '../../types/admin';
import './AdminStyles.css';

// Painel autocontido dos bots do Telegram: gerencia o próprio estado/listagem/CRUD.
// O backend registra o webhook de cada bot automaticamente no create (getMe + setWebhook).
export const TelegramBotsPanel: React.FC = () => {
  const [bots, setBots] = useState<TelegramBot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [editing, setEditing] = useState<TelegramBot | undefined>();
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [testingId, setTestingId] = useState<number | null>(null);

  const loadBots = async (page: number = 0) => {
    try {
      setLoading(true);
      setError('');
      const data = await telegramBotsApi.list(page, 20);
      setBots(data.bots ?? []);
      setCurrentPage(page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar bots');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBots(0);
  }, []);

  const flashSuccess = (msg: string) => {
    setSuccess(msg);
    setTimeout(() => setSuccess(''), 4000);
  };

  const handleCreate = async (formData: any) => {
    try {
      setSubmitting(true);
      setError('');
      const created = await telegramBotsApi.create(formData);
      flashSuccess('Bot do Telegram criado e webhook configurado automaticamente.');
      setShowForm(false);
      await loadBots(0);
      // O webhook já foi registrado pelo backend; mostra o link público para divulgar.
      if (created.link) {
        window.alert('Bot pronto! Divulgue este link para os clientes iniciarem a conversa:\n\n' + created.link);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao criar bot');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (formData: any) => {
    if (!editing) return;
    try {
      setSubmitting(true);
      setError('');
      await telegramBotsApi.update(editing.id, formData);
      flashSuccess('Bot atualizado com sucesso');
      setEditing(undefined);
      setShowForm(false);
      await loadBots(currentPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao atualizar bot');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      setError('');
      await telegramBotsApi.delete(id);
      flashSuccess('Bot desativado com sucesso');
      await loadBots(currentPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao deletar bot');
    }
  };

  const handleToggle = async (id: number) => {
    try {
      setError('');
      await telegramBotsApi.toggle(id);
      await loadBots(currentPage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao alternar status');
    }
  };

  const handleTest = async (id: number) => {
    // O bot não pode iniciar conversa: precisa de um chat_id que já tenha dado /start.
    const chatId = window.prompt('Informe o chat_id (numérico) de um usuário que já iniciou o bot com /start:');
    if (!chatId || !/^\d+$/.test(chatId.trim())) {
      if (chatId !== null) setError('chat_id inválido (precisa ser numérico).');
      return;
    }
    try {
      setError('');
      setTestingId(id);
      const result = await telegramBotsApi.test(id, chatId.trim());
      flashSuccess(`Mensagem de teste enviada para o chat ${result.to}.`);
    } catch (err: any) {
      // O backend devolve o motivo real em { error } (token errado, usuário não iniciou o bot, etc.).
      let msg = 'Falha ao testar o bot';
      try {
        const body = err?.target?.responseText ? JSON.parse(err.target.responseText) : null;
        if (body?.error) msg = body.error;
      } catch { /* mantém msg padrão */ }
      if (err instanceof Error && err.message) msg = err.message;
      setError(msg);
    } finally {
      setTestingId(null);
    }
  };

  const handleEdit = (bot: TelegramBot) => {
    setEditing(bot);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditing(undefined);
  };

  return (
    <div className="tab-content">
      {error && <div className="error-message">⚠️ {error}</div>}
      {success && <div className="success-message">✓ {success}</div>}

      <div className="content-section">
        <h3 className="section-title">Bots do Telegram</h3>

        <div className="success-message" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span>ℹ️ Crie o bot no <b>@BotFather</b>, cole o token aqui e escolha um V8. O webhook é configurado automaticamente — o cliente inicia pelo link <b>t.me/seu_bot</b>.</span>
        </div>

        {!showForm && (
          <button className="add-account-button" onClick={() => setShowForm(true)}>
            + Adicionar Bot
          </button>
        )}

        <div style={{ marginTop: '20px' }}>
          <TelegramBotsList
            bots={bots}
            loading={loading}
            testingId={testingId}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onToggle={handleToggle}
            onTest={handleTest}
          />
        </div>
      </div>

      <div className="content-section">
        {showForm ? (
          <TelegramBotForm
            bot={editing}
            onSubmit={editing ? handleUpdate : handleCreate}
            onCancel={handleCancel}
            loading={submitting}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#999' }}>
            <p>Selecione um bot para editar ou clique em "Adicionar Bot"</p>
          </div>
        )}
      </div>
    </div>
  );
};
